import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, Toaster } from "sonner";

import Header from "../components/Header";
import { api, formatApiErrorDetail } from "../lib/api";
import { formatDateShort } from "../lib/currency";

const STATUSES = ["active", "trialing", "past_due", "canceled", "incomplete", "unpaid"];

export default function AdminSubscriptions() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [formStatus, setFormStatus] = useState("active");
  const [formPlan, setFormPlan] = useState("monthly");
  const [formSeats, setFormSeats] = useState(3);
  const [formNote, setFormNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.get("/admin/subscriptions"),
        api.get("/admin/subscriptions/audit"),
      ]);
      setRows(r.data);
      setAudit(a.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openOverride = (row) => {
    setTarget(row);
    setFormStatus(row.subscription_status === "active" ? "active" : "active");
    setFormPlan(row.plan || "monthly");
    setFormSeats(row.seats_included || 3);
    setFormNote("");
  };

  const submitOverride = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post(`/admin/agencies/${target.org_id}/subscription/override`, {
        status: formStatus, plan: formPlan, seats_included: formSeats, note: formNote,
      });
      toast.success(t("admin_subs.override_saved"));
      setTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    } finally { setBusy(false); }
  };

  const clearOverride = async (row) => {
    if (!window.confirm(t("admin_subs.confirm_clear"))) return;
    try {
      await api.post(`/admin/agencies/${row.org_id}/subscription/clear-override`);
      toast.success(t("admin_subs.cleared"));
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic"));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-subs-page">
      <Header />
      <Toaster richColors position="top-right" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-700">{t("admin_subs.eyebrow")}</p>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl" data-testid="admin-subs-title">
            {t("admin_subs.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("admin_subs.subtitle")}</p>
        </div>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-500">{t("common.loading")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="py-2 text-left">{t("admin_subs.col_org")}</th>
                  <th className="py-2 text-left">{t("admin_subs.col_status")}</th>
                  <th className="py-2 text-left">{t("admin_subs.col_plan")}</th>
                  <th className="py-2 text-left">{t("admin_subs.col_renewal")}</th>
                  <th className="py-2 text-left">{t("admin_subs.col_seats")}</th>
                  <th className="py-2 text-left">{t("admin_subs.col_source")}</th>
                  <th className="py-2 text-right">{t("admin_subs.col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.org_id} className="border-t border-slate-100" data-testid={`admin-sub-row-${r.org_id}`}>
                    <td className="py-2 text-slate-900" data-testid={`admin-sub-name-${r.org_id}`}>{r.org_name}</td>
                    <td className="py-2">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200" data-testid={`admin-sub-status-${r.org_id}`}>
                        {r.subscription_status}
                      </span>
                    </td>
                    <td className="py-2 text-slate-600">{r.plan || "—"}</td>
                    <td className="py-2 text-slate-500">{formatDateShort(r.current_period_end)}</td>
                    <td className="py-2 text-slate-500">{r.seats_included}</td>
                    <td className="py-2 text-xs uppercase tracking-wider text-slate-500" data-testid={`admin-sub-source-${r.org_id}`}>{r.source}</td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openOverride(r)}
                          className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                          data-testid={`admin-sub-override-${r.org_id}`}
                        >
                          {t("admin_subs.override")}
                        </button>
                        {r.source === "admin_override" && (
                          <button
                            onClick={() => clearOverride(r)}
                            className="rounded border border-rose-200 bg-white px-2 py-0.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                            data-testid={`admin-sub-clear-${r.org_id}`}
                          >
                            {t("admin_subs.clear")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-subs-audit">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t("admin_subs.audit_title")}</h2>
          {audit.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">{t("admin_subs.audit_empty")}</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {audit.slice(0, 30).map((a) => (
                <li key={a.id} className="flex items-baseline gap-3 text-slate-700" data-testid={`admin-audit-${a.id}`}>
                  <span className="text-xs uppercase tracking-wider text-slate-500">{formatDateShort(a.created_at)}</span>
                  <span className="font-medium">{a.org_name}</span>
                  <span className="text-slate-500">·</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">{a.action}</span>
                  {a.status && <span className="text-xs text-slate-500">{a.status}/{a.plan}</span>}
                  <span className="ml-auto text-xs text-slate-400">{a.admin_email}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {target && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="admin-override-modal">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-slate-900">{t("admin_subs.modal_title", { org: target.org_name })}</h3>
              <p className="mt-1 text-xs text-slate-500">{t("admin_subs.modal_hint")}</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin_subs.f_status")}</label>
                  <select value={formStatus} onChange={(e) => setFormStatus(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-override-status">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin_subs.f_plan")}</label>
                  <select value={formPlan} onChange={(e) => setFormPlan(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-override-plan">
                    <option value="monthly">monthly</option>
                    <option value="annual">annual</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin_subs.f_seats")}</label>
                  <input type="number" min={1} max={100} value={formSeats} onChange={(e) => setFormSeats(Number(e.target.value))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-override-seats" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{t("admin_subs.f_note")}</label>
                  <textarea rows={2} value={formNote} onChange={(e) => setFormNote(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" data-testid="admin-override-note" />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setTarget(null)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="admin-override-cancel">
                  {t("common.cancel")}
                </button>
                <button onClick={submitOverride} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60" data-testid="admin-override-save">
                  {busy ? t("common.loading") : t("admin_subs.save_override")}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
